'use client';

import { Search } from 'lucide-react';

function TabsSkeleton() {
  return (
    <div className="flex gap-1 overflow-x-auto pb-2 md:pb-0 mb-6">
      {Array.from({ length: 5 }).map((_, i) => (
        <div
          key={i}
          className="h-10 rounded-lg bg-stone-100 animate-pulse"
          style={{ width: `${80 + i * 10}px` }}
        />
      ))}
    </div>
  );
}

function ResultRowsSkeleton() {
  return (
    <div className="bg-white rounded-xl border border-stone-200 shadow-sm overflow-hidden">
      <div className="divide-y divide-stone-100">
        {Array.from({ length: 5 }).map((_, i) => (
          <div key={i} className="p-4 md:px-6 md:py-4 animate-pulse">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-stone-200" />
              <div className="flex-1 space-y-2">
                <div className="h-4 bg-stone-200 rounded w-1/3" />
                <div className="h-3 bg-stone-100 rounded w-1/4" />
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export function SearchPageSkeleton({ query }: { query?: string }) {
  // Results state: compact search bar + tabs + rows (matches actual SearchPage results layout)
  if (query) {
    return (
      <div className="flex-1 flex flex-col">
        {/* Header placeholder — matches PublicHeader height + bg */}
        <div className="h-20 bg-stone-950" />

        {/* Compact search bar — matches sticky bar in SearchPage */}
        <div className="bg-white border-b border-stone-200">
          <div className="container max-w-6xl py-3">
            <div className="relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-stone-400" />
              <div className="h-10 pl-12 pr-24 text-sm bg-stone-50 border border-stone-200 rounded-md flex items-center">
                <span className="text-stone-900 text-sm">{query}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Results skeleton */}
        <section className="py-8 bg-dgr-cream flex-1">
          <div className="container max-w-6xl">
            <div className="h-4 bg-stone-200/60 rounded w-48 mb-4 animate-pulse" />
            <TabsSkeleton />
            <ResultRowsSkeleton />
          </div>
        </section>
      </div>
    );
  }

  // Landing state: hero with search bar (no query yet)
  return (
    <div className="flex-1 flex flex-col">
      {/* Header placeholder */}
      <div className="h-20 bg-stone-950" />

      {/* Hero skeleton */}
      <section className="relative h-[280px] md:h-[360px] bg-gradient-to-t from-stone-900 via-stone-800 to-stone-700">
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-center w-full max-w-2xl px-4">
            <div className="flex items-center justify-center gap-2 md:gap-3 mb-4 md:mb-6">
              <Search className="w-7 h-7 md:w-10 md:h-10 text-[#c5b358]" />
              <h1 className="text-2xl md:text-5xl font-bold text-white">Search</h1>
            </div>

            {/* Search bar skeleton */}
            <div className="relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-stone-400" />
              <div className="h-14 pl-12 pr-24 text-lg bg-white/95 backdrop-blur rounded-md shadow-lg" />
            </div>
          </div>
        </div>
      </section>

      {/* Empty state skeleton */}
      <section className="py-8 bg-dgr-cream flex-1">
        <div className="container max-w-6xl">
          <div className="py-16 text-center">
            <div className="w-16 h-16 rounded-full bg-stone-200 mx-auto mb-6 animate-pulse" />
            <div className="h-6 bg-stone-200 rounded w-64 mx-auto mb-2 animate-pulse" />
            <div className="h-4 bg-stone-100 rounded w-80 mx-auto animate-pulse" />
          </div>
        </div>
      </section>
    </div>
  );
}
